import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authedPatch } from '@/lib/api';
import type { ProjectResponse, UpdateProjectInput } from '@releve/shared';
import { useOrganization } from '@/contexts/OrganizationContext';

export interface UpdateProjectData {
  id: string;
  description?: string;
  name?: string;
  status?: string;
  start_date?: string | null;
  end_date?: string | null;
  planning_start_date?: string | null;
  preproduction_start_date?: string | null;
  postevent_end_date?: string | null;
  budget?: number | null;
  /** NOT NULL column — the PATCH schema refuses null, so it is never cleared. */
  project_type_id?: string;
  artistic_director_id?: string | null;
  sustainability_score?: number | null;
  carbon_footprint_level?: string | null;
  local_sourcing_percentage?: number | null;
  sustainability_notes?: string | null;
}

/** Keys the PATCH body may carry — the strict `updateProjectSchema` whitelist. */
type UpdateProjectPayload = Partial<Record<keyof UpdateProjectInput, unknown>>;

/**
 * Update a project through `PATCH /api/v1/projects/orgs/:orgId/projects/:id`
 * (Pattern B — project writes are backend-only). Only the fields the caller
 * provides are sent: `undefined` means "leave alone", `null` means "clear".
 * The server validates the body against the strict `updateProjectSchema`
 * (which never accepts `organization_id`, `created_by`, `code` or the retired
 * `team_size`) and returns `{ project }`.
 */
export function useUpdateProject() {
  const { selectedOrganization } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateProjectData) => {
      if (!selectedOrganization) {
        throw new Error('No organization selected');
      }

      const payload: UpdateProjectPayload = {};

      // Only include fields that are provided
      if (data.description !== undefined) payload.description = data.description || null;
      if (data.name !== undefined) payload.name = data.name;
      if (data.status !== undefined) payload.status = data.status;
      if (data.start_date !== undefined) payload.start_date = data.start_date;
      if (data.end_date !== undefined) payload.end_date = data.end_date;
      if (data.planning_start_date !== undefined) payload.planning_start_date = data.planning_start_date;
      if (data.preproduction_start_date !== undefined) payload.preproduction_start_date = data.preproduction_start_date;
      if (data.postevent_end_date !== undefined) payload.postevent_end_date = data.postevent_end_date;
      if (data.budget !== undefined) payload.budget = data.budget;
      if (data.project_type_id !== undefined) payload.project_type_id = data.project_type_id;
      if (data.artistic_director_id !== undefined) payload.artistic_director_id = data.artistic_director_id || null;
      if (data.sustainability_score !== undefined) payload.sustainability_score = data.sustainability_score;
      if (data.carbon_footprint_level !== undefined) payload.carbon_footprint_level = data.carbon_footprint_level;
      if (data.local_sourcing_percentage !== undefined) payload.local_sourcing_percentage = data.local_sourcing_percentage;
      if (data.sustainability_notes !== undefined) payload.sustainability_notes = data.sustainability_notes;

      // The PATCH schema refuses an empty body; fail here rather than make a
      // round trip that can only 400.
      if (Object.keys(payload).length === 0) {
        throw new Error('No project fields to update');
      }

      const result = await authedPatch<ProjectResponse>(
        `/api/v1/projects/orgs/${selectedOrganization.id}/projects/${data.id}`,
        payload,
      );
      return result.project;
    },
    onSuccess: () => {
      // The org prefix covers the index grid (['projects', orgId, 'index', {}])
      // and the useProjects pickers.
      void queryClient.invalidateQueries({
        queryKey: ['projects', selectedOrganization?.id],
      });
    },
    onError: (error: unknown) => {
      console.error('Failed to update project:', error);
    },
  });
}
